import { useState, useCallback } from 'react';

/**
 * Expected Place Details API Response Structure
 * Based on AutoCompletePlaceDetail class from backend
 */
export const PlaceDetailSchema = {
  place_id: 'string',           // Google place ID or DZIP-xxxxx for domestic zips
  name: 'string',               // Place name
  formatted_address: 'string',  // Full formatted address
  street: 'string',             // Street address
  city: 'string',               // City name
  state: 'string',              // State name or code
  country: 'string',            // Full country name
  country_code: 'string',       // ISO country code (US, CA, etc.)
  zip: 'string',                // Postal/ZIP code
  lat: 'number',                // Latitude
  lng: 'number',                // Longitude
  sub_types: 'array',           // Special subtypes (e.g., ["shipology_domestic_zip"])
  types: 'array',               // Google Place types
  subpremise: 'string'          // Subpremise (apartment, suite, etc.)
};

/**
 * Gets CSRF token from Rails meta tag
 * @returns {string} CSRF token or empty string if not found
 */
const getCSRFToken = () => {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : '';
};

/**
 * Custom hook for fetching place details from Google Places API
 * @returns {Object} Hook with fetchPlaceDetails function and loading/error states
 */
export const usePlaceDetails = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const extractCityFromFormattedAddress = (formattedAddress) => {
    const match = formattedAddress.match(/,\s*([A-Za-z\s]+),\s*([A-Z]{2})\s*\d{5}/);
    return match ? match[1] : null;
  };

  /**
   * Fetches place details for a given place_id
   * @param {string} placeId - Google place ID or DZIP-xxxxx for domestic zips
   * @param {string} sessionToken - Optional session token for Places API billing optimization
   * @returns {Promise<Object>} Place details response
   */
  const fetchPlaceDetails = useCallback(async (placeId, sessionToken = null) => {
    if (!placeId) {
      const validationError = 'Place ID is required';
      setError(validationError);
      throw new Error(validationError);
    }

    setLoading(true);
    setError(null);

    try {
      // Get CSRF token from meta tag
      const csrfToken = getCSRFToken();
      
      // Build URL with place_id parameter
      const url = `/api/v5/auto-complete/place-details/${encodeURIComponent(placeId)}`;
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (sessionToken) {
        queryParams.append('sessiontoken', sessionToken);
      }
      
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;

      // Make API call
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          // Include CSRF token in header for Rails
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        credentials: 'same-origin' // Include cookies for Rails session
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Validate response structure
      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format from place details API');
      }

      setData(result);
      
      if(result.city == null && result.formatted_address) {
        result.city = extractCityFromFormattedAddress(result.formatted_address);
      }
      return result;

    } catch (err) {
      console.error('Place details API error:', err);
      const errorMessage = err.message || 'Failed to fetch place details';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Checks if a place_id is a domestic ZIP code format
   * @param {string} placeId - Place ID to check
   * @returns {boolean} True if it's a domestic ZIP format (DZIP-xxxxx)
   */
  const isDomesticZip = useCallback((placeId) => {
    return placeId && placeId.startsWith('DZIP-');
  }, []);

  /**
   * Extracts ZIP code from domestic ZIP place_id
   * @param {string} placeId - Domestic ZIP place_id (DZIP-xxxxx format)
   * @returns {string} ZIP code or empty string
   */
  const extractZipFromPlaceId = useCallback((placeId) => {
    if (!isDomesticZip(placeId)) return '';
    return placeId.replace('DZIP-', '').trim();
  }, [isDomesticZip]);

  /**
   * Validates place details response
   * @param {Object} placeDetails - Place details object to validate
   * @returns {Object} Validation result
   */
  const validatePlaceDetails = useCallback((placeDetails) => {
    if (!placeDetails || typeof placeDetails !== 'object') {
      return { isValid: false, message: 'Invalid place details object' };
    }

    const required = ['place_id', 'formatted_address'];
    const missing = required.filter(field => !placeDetails[field]);

    if (missing.length > 0) {
      return { 
        isValid: false, 
        message: `Missing required fields: ${missing.join(', ')}` 
      };
    }

    // Additional validation for US addresses
    if (placeDetails.country_code === 'US') {
      if (!placeDetails.city || !placeDetails.state) {
        return { 
          isValid: false, 
          message: 'US addresses must have city and state' 
        };
      }
    }

    return { isValid: true, message: 'Valid' };
  }, []);

  return {
    fetchPlaceDetails,
    loading,
    error,
    data,
    isDomesticZip,
    extractZipFromPlaceId,
    validatePlaceDetails,
    // Helper to reset state
    reset: () => {
      setData(null);
      setError(null);
      setLoading(false);
    },
    // Expose CSRF token getter for debugging
    getCSRFToken: () => getCSRFToken()
  };
};


/**
 * Utility function to format place details for display
 * @param {Object} placeDetails - Place details object
 * @returns {Object} Formatted address components
 */
export const formatPlaceDetails = (placeDetails) => {
  if (!placeDetails) return null;

  return {
    // Full formatted address
    fullAddress: placeDetails.formatted_address,
    
    // Address components
    street: placeDetails.street,
    city: placeDetails.city,
    state: placeDetails.state,
    zip: placeDetails.zip,
    country: placeDetails.country,
    countryCode: placeDetails.country_code,
    
    // Coordinates
    coordinates: placeDetails.lat && placeDetails.lng ? {
      lat: placeDetails.lat,
      lng: placeDetails.lng
    } : null,
    
    // Metadata
    placeId: placeDetails.place_id,
    name: placeDetails.name,
    types: placeDetails.types || [],
    subTypes: placeDetails.sub_types || [],
    
    // Helper properties
    isDomesticZip: placeDetails.sub_types?.includes('shipology_domestic_zip') || false,
    isUS: placeDetails.country_code === 'US',
    isCanada: placeDetails.country_code === 'CA'
  };
};